import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Calendar, User, ArrowLeft, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { HtmlContent } from '../components/RichTextEditor';
import ShareButtons from '../components/ShareButtons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function HaberDetay() {
  const { id } = useParams();
  const [news, setNews] = useState(null);
  const [allNews, setAllNews] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    fetchNewsDetail();
    fetchAllNews();
    fetchComments();
    checkUser();
    // Scroll to top on news change
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  const checkUser = () => {
    const token = localStorage.getItem('token');
    if (token) {
      fetch(`${BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => res.json())
        .then(data => {
          if (data.id) setUser(data);
        })
        .catch(() => {});
    }
  };

  const fetchNewsDetail = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/news/${id}`);
      if (response.ok) {
        const data = await response.json();
        setNews(data);
      } else {
        toast.error('Haber bulunamadı');
      }
    } catch (error) {
      toast.error('Haber yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllNews = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/news`);
      if (r.ok) {
        const data = await r.json();
        if (Array.isArray(data)) setAllNews(data);
      }
    } catch { /* ignore */ }
  };

  const fetchComments = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/news/${id}/comments`);
      if (response.ok) {
        const data = await response.json();
        setComments(data);
      }
    } catch (error) {
      console.error('Yorumlar yüklenemedi:', error);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Yorum yapmak için giriş yapmalısınız');
      return;
    }

    if (!newComment.trim()) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/news/${id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ comment: newComment })
      });

      if (response.ok) {
        toast.success('Yorum eklendi');
        setNewComment('');
        fetchComments();
      } else {
        toast.error('Yorum eklenemedi');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/news/${id}/comments/${commentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Yorum silindi');
        fetchComments();
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center">
        <div className="text-xl text-amber-800">Yükleniyor...</div>
      </div>
    );
  }

  if (!news) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-lg text-amber-700 mb-4">Haber bulunamadı</p>
          <Link to="/haberler">
            <Button className="bg-amber-600 hover:bg-amber-700">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Haberlere Dön
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Determine prev/next news (allNews assumed sorted by created_at desc from backend)
  const currentIndex = allNews.findIndex(n => n.id === news.id);
  // In a desc-sorted list: index-1 is newer (next chronologically newer), index+1 is older (previous)
  // Use intuitive labels: "Önceki" = older (next index), "Sonraki" = newer (previous index)
  const olderNews = currentIndex >= 0 && currentIndex < allNews.length - 1 ? allNews[currentIndex + 1] : null;
  const newerNews = currentIndex > 0 ? allNews[currentIndex - 1] : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/haberler">
          <Button variant="ghost" className="mb-6 text-amber-800 hover:bg-amber-50">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Haberlere Dön
          </Button>
        </Link>

        <Card className="border-2 border-amber-200 bg-white shadow-xl mb-8">
          {news.image_url && (
            <div className="w-full h-96 overflow-hidden rounded-t-lg">
              <img
                src={news.image_url}
                alt={news.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <CardHeader>
            <CardTitle className="text-3xl text-amber-900" style={{ fontFamily: 'Merriweather, serif' }}>
              {news.title}
            </CardTitle>
            <div className="flex flex-wrap gap-4 text-sm text-amber-600 mt-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(news.created_at)}</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>Yazar: {news.author}</span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <HtmlContent html={news.content} className="text-amber-800 text-lg leading-relaxed" />
            <div className="mt-8 pt-6 border-t border-amber-200">
              <ShareButtons title={news.title} />
            </div>
          </CardContent>
        </Card>

        {/* Prev / Next news navigation */}
        {(olderNews || newerNews) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-8" data-testid="news-prev-next">
            {olderNews ? (
              <Link
                to={`/haberler/${olderNews.id}`}
                className="group flex items-center gap-3 p-3 bg-white border border-amber-200 hover:border-amber-400 rounded-2xl shadow-sm hover:shadow-md transition-all"
                data-testid="prev-news-link"
              >
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl overflow-hidden bg-amber-100 flex-shrink-0 flex items-center justify-center">
                  {olderNews.image_url ? (
                    <img src={olderNews.image_url} alt={olderNews.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                  ) : (
                    <ChevronLeft className="w-6 h-6 text-amber-400" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] uppercase tracking-wider text-amber-600 font-semibold mb-0.5 flex items-center gap-1">
                    <ChevronLeft className="w-3 h-3" /> Önceki Haber
                  </p>
                  <p className="text-sm font-semibold text-amber-900 line-clamp-2 group-hover:text-orange-600 transition-colors">
                    {olderNews.title}
                  </p>
                </div>
              </Link>
            ) : <div />}
            {newerNews ? (
              <Link
                to={`/haberler/${newerNews.id}`}
                className="group flex items-center gap-3 p-3 bg-white border border-amber-200 hover:border-amber-400 rounded-2xl shadow-sm hover:shadow-md transition-all sm:text-right"
                data-testid="next-news-link"
              >
                <div className="min-w-0 flex-1 order-2 sm:order-1">
                  <p className="text-[11px] uppercase tracking-wider text-amber-600 font-semibold mb-0.5 flex items-center gap-1 sm:justify-end">
                    Sonraki Haber <ChevronRight className="w-3 h-3" />
                  </p>
                  <p className="text-sm font-semibold text-amber-900 line-clamp-2 group-hover:text-orange-600 transition-colors">
                    {newerNews.title}
                  </p>
                </div>
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl overflow-hidden bg-amber-100 flex-shrink-0 order-1 sm:order-2 flex items-center justify-center">
                  {newerNews.image_url ? (
                    <img src={newerNews.image_url} alt={newerNews.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                  ) : (
                    <ChevronRight className="w-6 h-6 text-amber-400" />
                  )}
                </div>
              </Link>
            ) : <div />}
          </div>
        )}

        {/* Comments Section */}
        <Card className="border-2 border-amber-200 bg-white">
          <CardHeader>
            <CardTitle className="text-2xl text-amber-900" style={{ fontFamily: 'Merriweather, serif' }}>
              Yorumlar ({comments.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Add Comment Form */}
            {user ? (
              <form onSubmit={handleAddComment} className="mb-8">
                <Textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Yorumunuzu yazın..."
                  rows={4}
                  className="border-amber-300 focus:border-amber-500 mb-3"
                  data-testid="comment-input"
                />
                <Button
                  type="submit"
                  className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700"
                  data-testid="add-comment-btn"
                >
                  Yorum Ekle
                </Button>
              </form>
            ) : (
              <div className="mb-8 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-amber-800">Yorum yapmak için <Link to="/uyelik" className="text-orange-600 font-semibold hover:underline">giriş yapın</Link></p>
              </div>
            )}

            {/* Comments List */}
            <div className="space-y-4">
              {comments.length === 0 ? (
                <p className="text-center text-amber-600 py-8">Henüz yorum yapılmamış. İlk yorumu siz yapın!</p>
              ) : (
                comments.map((comment) => (
                  <div
                    key={comment.id}
                    className="p-4 bg-amber-50 rounded-lg border border-amber-200"
                    data-testid={`comment-${comment.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-amber-600 to-orange-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                          {comment.user_nickname.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-amber-900">{comment.user_nickname}</p>
                          <p className="text-xs text-amber-600">{formatDate(comment.created_at)}</p>
                        </div>
                      </div>
                      {(user?.id === comment.user_id || user?.role === 'admin') && (
                        <Button
                          onClick={() => handleDeleteComment(comment.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-comment-${comment.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <p className="text-amber-800 ml-10">{comment.comment}</p>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
